// (c) Copyright Ascensio System SIA 2009-2025
//
// This program is a free software product.
// You can redistribute it and/or modify it under the terms
// of the GNU Affero General Public License (AGPL) version 3 as published by the Free Software
// Foundation. In accordance with Section 7(a) of the GNU AGPL its Section 15 shall be amended
// to the effect that Ascensio System SIA expressly excludes the warranty of non-infringement of
// any third-party rights.
//
// This program is distributed WITHOUT ANY WARRANTY, without even the implied warranty
// of MERCHANTABILITY or FITNESS FOR A PARTICULAR  PURPOSE. For details, see
// the GNU AGPL at: http://www.gnu.org/licenses/agpl-3.0.html
//
// You can contact Ascensio System SIA at Lubanas st. 125a-25, Riga, Latvia, EU, LV-1021.
//
// The  interactive user interfaces in modified source and object code versions of the Program must
// display Appropriate Legal Notices, as required under Section 5 of the GNU AGPL version 3.
//
// Pursuant to Section 7(b) of the License you must retain the original Product logo when
// distributing the program. Pursuant to Section 7(e) we decline to grant you any rights under
// trademark law for use of our trademarks.
//
// All the Product's GUI elements, including illustrations and icon sets, as well as technical writing
// content are licensed under the terms of the Creative Commons Attribution-ShareAlike 4.0
// International. See the License terms at http://creativecommons.org/licenses/by-sa/4.0/legalcode

import { Trans } from "react-i18next";
import type { TFunction } from "i18next";
import { match, P } from "ts-pattern";

import PublicRoomIconURL from "PUBLIC_DIR/images/icons/32/room/public.svg?url";

import { Text } from "../../../components/text";
import { LinkSharingEntityType } from "../../../enums";
import { iconSize32 } from "../../../utils/image-helpers";
import type { TValidateShareRoom } from "../../../api/rooms/types";

/**
 * Helper types for password-protected resources descriptions
 */
export const getPasswordDescription = (
  t: TFunction,
  validationData: TValidateShareRoom,
) => {
  const displayText = validationData.entityTitle || validationData.title || "";

  return match(validationData)
    .with({ type: LinkSharingEntityType.File }, () => (
      <Trans
        t={t}
        ns="Common"
        i18nKey="Common:PasswordProtectedFile"
        values={{ fileName: displayText }}
        components={{
          1: (
            <Text
              key={displayText}
              as="strong"
              fontSize="13px"
              fontWeight="600"
            />
          ),
        }}
      />
    ))
    .with(
      {
        isRoom: true,
        type: LinkSharingEntityType.RoomOrFolder,
        entityType: LinkSharingEntityType.File,
      },
      () => (
        <Trans
          t={t}
          ns="Common"
          i18nKey="Common:PasswordProtectedRoomFile"
          values={{ fileName: displayText }}
          components={{
            1: (
              <Text
                key={displayText}
                as="strong"
                fontSize="13px"
                fontWeight="600"
              />
            ),
          }}
        />
      ),
    )
    .with(
      {
        isRoom: false,
        type: LinkSharingEntityType.RoomOrFolder,
        entityType: LinkSharingEntityType.File,
      },
      () => (
        <Trans
          t={t}
          ns="Common"
          i18nKey="Common:PasswordProtectedFolderFile"
          values={{ fileName: displayText }}
          components={{
            1: (
              <Text
                key={displayText}
                as="strong"
                fontSize="13px"
                fontWeight="600"
              />
            ),
          }}
        />
      ),
    )
    .with(
      {
        isRoom: true,
        type: LinkSharingEntityType.RoomOrFolder,
        entityType: LinkSharingEntityType.RoomOrFolder,
      },
      () => (
        <Trans
          t={t}
          ns="Common"
          i18nKey="Common:PasswordProtectedRoomFolder"
          values={{ folderName: displayText }}
          components={{
            1: (
              <Text
                key={displayText}
                as="strong"
                fontSize="13px"
                fontWeight="600"
              />
            ),
          }}
        />
      ),
    )
    .with(
      {
        isRoom: false,
        type: LinkSharingEntityType.RoomOrFolder,
        entityType: LinkSharingEntityType.RoomOrFolder,
      },
      () => (
        <Trans
          t={t}
          ns="Common"
          i18nKey="Common:PasswordProtectedFolderFolder"
          values={{ folderName: displayText }}
          components={{
            1: (
              <Text
                key={displayText}
                as="strong"
                fontSize="13px"
                fontWeight="600"
              />
            ),
          }}
        />
      ),
    )
    .with(
      {
        isRoom: true,
        type: LinkSharingEntityType.RoomOrFolder,
        entityType: P.nullish,
      },
      () => t("Common:NeedPassword"),
    )
    .with(
      {
        isRoom: false,
        type: LinkSharingEntityType.RoomOrFolder,
        entityType: P.nullish,
      },
      () => (
        <Trans
          t={t}
          ns="Common"
          i18nKey="Common:PasswordProtectedFolder"
          values={{ folderName: displayText }}
          components={{
            1: (
              <Text
                key={displayText}
                as="strong"
                fontSize="13px"
                fontWeight="600"
              />
            ),
          }}
        />
      ),
    )
    .when(
      () => !displayText && !validationData.entityType,
      () => t("Common:NeedPassword"),
    )
    .otherwise(() => t("Common:NeedPassword"));
};

export const getLogo = (
  validationData: TValidateShareRoom,
  getIcon?: (fileExst: string) => string,
) => {
  switch (validationData.type) {
    case LinkSharingEntityType.File: {
      const fileExtension = validationData.title.split(".").pop();

      if (!fileExtension) {
        return iconSize32.get("file.svg");
      }

      const path = `${fileExtension.replace(/^\./, "")}.svg`;

      return getIcon ? getIcon(`.${fileExtension}`) : iconSize32.get(path);
    }
    case LinkSharingEntityType.RoomOrFolder:
      if (validationData.isRoom) return PublicRoomIconURL;

      return iconSize32.get("folder.svg");
    default:
      return undefined;
  }
};
